import React, { FC } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { GenericMedia } from '@/declarations/GenericMedia';
import { GridViewEmployeeVariant } from '@/components/MediaList/ViewTypes/GridView/GridViewEmployeeVariant';
import { CommonMediaListViewTypeProps } from '../../CommonMediaListViewTypeProps';
import Styles from '../../../../assets/js/Styles';
import GridViewDefaultVariant from './GridViewDefaultVariant';
import GridViewDMVariant from './GridViewDMVariant';

export type GridViewProps = CommonMediaListViewTypeProps & {
  renderGridItemContent?: (item: GenericMedia<unknown>) => React.ReactNode;
};

export const GridView: FC<GridViewProps> = ({
  items,
  isSelectable,
  isFavouriteable,
  getIsFavourite,
  getIsSelected,
  onFavouriteChanged,
  onSelectChanged,
  variant = 'default',
  onItemClick,
  gridThumbnailWidth = 300,
  ...rest
}) => {
  if (onItemClick && isSelectable && onSelectChanged) {
    // eslint-disable-next-line no-console
    console.warn('[GridView] onItemClick and onSelectChanged are both supplied. onItemClick will be ignored.');
  }
  return (
    <Box
      role='list'
      sx={{
        gap: variant === 'card' ? 2.5 : 1,
        display: 'grid',
        p: 2,
        gridTemplateColumns: `repeat(auto-fill, ${gridThumbnailWidth}px)`,
        backgroundColor: variant === 'card' ? Styles.Colors.LIGHTEST_GREY : 'transparent',
      }}>
      {items.map((item) => {
        const isSelected = !!isSelectable && !!getIsSelected?.(item);
        const isFavourite = !!isFavouriteable && !!getIsFavourite?.(item);

        const variantProps = {
          item,
          isFavourite,
          isSelectable,
          isFavouriteable,
          getIsFavourite,
          getIsSelected,
          onFavouriteChanged,
          onSelectChanged,
          variant,
          onItemClick,
        };

        const onClick = () => {
          if (isSelectable && onSelectChanged) {
            onSelectChanged(item, !isSelected);
          }
          onItemClick?.(item);
        };
        const isClickable = isSelectable || !!onItemClick;

        return (
          <Box
            key={item.id}
            aria-label={item.title}
            role='listitem'
            sx={{
              width: gridThumbnailWidth,
              // Cards in a row get the same height; their own content stretches to fill it
              height: variant === 'card' ? 'auto' : 'fit-content',
              ...(variant === 'card' && {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 1.5px 4px rgba(0,0,0,0.12),0 1.5px 3px rgba(0,0,0,0.06)',
              }),
              border: variant === 'card' ? `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}` : '3px solid transparent',
              outline:
                isSelectable && isSelected
                  ? `4px solid ${Styles.Colors.MEDIA_ITEM_SELECTED_COLOR}`
                  : `0px solid transparent`,
              borderRadius: Styles.Dimensions.RADIUS_ROUND_SM,
              transition: 'all 150ms ease',
              justifyContent: 'center',
              backgroundColor: variant === 'dm' ? Styles.Colors.THEME_BG_COLOR_SECONDARY : undefined,
            }}>
            <ButtonBase
              onClick={onClick}
              aria-label={item.title}
              disabled={!isClickable}
              focusRipple
              style={{
                pointerEvents: isClickable || isFavouriteable ? 'auto' : 'none',
              }}
              sx={{ width: '100%', height: variant === 'card' ? 'auto' : '100%', flexShrink: 0 }}>
              {variant === 'dm' && <GridViewDMVariant {...variantProps} />}
              {variant === 'employee' && <GridViewEmployeeVariant {...variantProps} />}
              {variant !== 'dm' && variant !== 'employee' && <GridViewDefaultVariant {...variantProps} />}
            </ButtonBase>
            {rest.renderGridItemContent?.(item)}
          </Box>
        );
      })}
    </Box>
  );
};

export default GridView;
